import { fetchBaseQuery, type BaseQueryApi, type FetchArgs, type FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getCurrentSession } from "../../supabase/authService";

export interface CompletionIdentity {
  userId: string;
  organisationId: string | null;
}

export type CompletionResult<T> =
  | { data: T; error?: undefined }
  | { error: FetchBaseQueryError; data?: undefined };

// eslint-disable-next-line no-unused-vars
export type CompletionRequest = (args: string | FetchArgs) => Promise<CompletionResult<unknown>>;

export const identityChanged = (): CompletionResult<never> => ({
  error: { status: "CUSTOM_ERROR", error: "Completion identity changed", data: { error: "COMPLETION_IDENTITY_CHANGED" } },
});

/** A completion request must never pick up another account's token during prepareHeaders. */
export async function withCompletionIdentity<T>(
  expected: CompletionIdentity,
  api: BaseQueryApi,
  // eslint-disable-next-line no-unused-vars
  task: (request: CompletionRequest) => Promise<CompletionResult<T>>,
): Promise<CompletionResult<T>> {
  const session = await getCurrentSession();
  if (!session) {return { error: { status: 401, data: { error: "SIGN_IN_REQUIRED" } } };}
  if (session.user.id !== expected.userId) {return identityChanged();}

  // The credential stays in this closure, never in mutation arguments, Redux or recovery storage.
  const query = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BACKEND_BASE_URL,
    timeout: 30_000,
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      headers.set("Authorization", `Bearer ${session.access_token}`);
      return headers;
    },
  });
  const request: CompletionRequest = async (args) => {
    const current = await getCurrentSession();
    if (!current) {return { error: { status: 401, data: { error: "SIGN_IN_REQUIRED" } } };}
    if (current.user.id !== expected.userId) {return identityChanged();}
    const result = await query(args, api, {});
    const after = await getCurrentSession();
    if (after && after.user.id !== expected.userId) {return identityChanged();}
    if (result.error) {
      if (result.error.status === 429) {
        return { error: { ...result.error, data: {
          retry_after: result.meta?.response?.headers.get("Retry-After") || null,
        } } };
      }
      return { error: result.error };
    }
    return { data: result.data };
  };
  const profile = await request("/users/me");
  if (profile.error) {return profile;}
  const value = profile.data as { id?: string; organisation_id?: string | null } | null;
  if (!value || value.id !== expected.userId || value.organisation_id !== expected.organisationId) {
    return identityChanged();
  }
  const result = await task(request);
  if (result.error) { return result; }
  const finalProfile = await request("/users/me");
  if (finalProfile.error) { return finalProfile; }
  const finalIdentity = finalProfile.data as { id?: string; organisation_id?: string | null } | null;
  if (!finalIdentity || finalIdentity.id !== expected.userId || finalIdentity.organisation_id !== expected.organisationId) {
    return identityChanged();
  }
  return result;
}
